import { AlertCircle, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import { LoadingState } from "@/components/brand";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type { Artifact } from "../../caseIde/types";
import { useWorkflowsOptional } from "../../caseIde/workflows/context/WorkflowsContext";
import { KeyFactsAPI, keyFactErrorText } from "../api";
import { useInsightsFlow } from "../hooks/useInsightsFlow";
import { useKeyFactActions } from "../hooks/useKeyFactActions";
import { useKeyFacts } from "../hooks/useKeyFacts";
import { useNoticeBrief } from "../hooks/useNoticeBrief";
import {
  NOTICE_WORKFLOWS,
  insightCounts,
  noticeLabelOf,
  presentSections,
  reviewQueue
} from "../model";
import type { KeyFact, KeyFactsCase, KeyFactsView } from "../types";
import { FactReference } from "./FactReference";
import { NeedsYouStrip } from "./NeedsYouStrip";
import { NoticeBrief } from "./notice/NoticeBrief";
import { OnThisCase } from "./OnThisCase";

const UPDATING_STATES = new Set(["pending", "running"]);

const EXPECTING: Record<string, string> = {
  rfe: "RFE",
  noir_noid: "NOID/NOIR"
};

/**
 * Key Facts: the values the case's filings rest on. The Insights tab shows
 * every fact by section, each asking right under itself when it needs the
 * attorney, with a strip that counts them and steps to the next; the Notice
 * Brief and the case's own switches sit beside it. Live over `/api/events`.
 */
export const KeyFacts: FC<{
  individualId: string;
  onOpen: (artifact: Artifact) => void;
  /** Seed the case chat (Request From Client). */
  onPrompt?: (text: string) => void;
  /** Open a draft at a passage (Answered in Response). */
  onReveal?: (draftId: string, text: string) => void;
  /** Add the document a missing fact needs to the case's files. */
  onUpload?: (fact: KeyFact) => void;
}> = ({ individualId, onOpen, onPrompt, onReveal, onUpload }) => {
  const { data, loading, error, refetch, put } = useKeyFacts(individualId);
  const actions = useKeyFactActions(individualId, put, refetch);
  const [view, setView] = useState<KeyFactsView>("facts");
  const noticeCase = NOTICE_WORKFLOWS.has(data?.workflow);
  const { data: noticeBrief } = useNoticeBrief(individualId, noticeCase);
  const expecting =
    noticeBrief?.brief?.label ??
    noticeLabelOf(data?.facts ?? []) ??
    EXPECTING[data?.workflow ?? ""] ??
    "Notice";
  const [landed, setLanded] = useState(false);
  useEffect(() => {
    if (!data || landed) return;
    setLanded(true);
    if (noticeCase) setView("brief");
  }, [data, landed, noticeCase]);
  const [refreshing, setRefreshing] = useState(false);

  const facts = useMemo(() => data?.facts ?? [], [data]);
  const workflow = data?.workflow;
  const sections = useMemo(() => (data ? presentSections(data) : []), [data]);
  const queue = useMemo(
    () => reviewQueue(facts, workflow, sections),
    [facts, workflow, sections]
  );
  const counts = useMemo(
    () => insightCounts(facts, workflow),
    [facts, workflow]
  );
  const flow = useInsightsFlow(queue, actions, { onPrompt, onUpload });
  const updating = !!data && UPDATING_STATES.has(data.settings.reconcile_state);

  // Insights read the files the organizer has classified, so files still in
  // Unsorted can't answer "Missing: the passport". Recheck organizes just
  // those first; the apply triggers the server's own reconcile.
  const workflows = useWorkflowsOptional();
  const unclassified = useMemo(
    () => workflows?.facts.unsortedFiles ?? [],
    [workflows]
  );
  const organizing = unclassified.length > 0 && !!workflows;
  const recheckLabel = organizing
    ? `Organize ${unclassified.length} New ${
        unclassified.length === 1 ? "File" : "Files"
      } and Recheck`
    : "Recheck";

  const refresh = async () => {
    setRefreshing(true);
    try {
      if (workflows && organizing) {
        workflows.run("organize", {
          kind: "selection",
          nodeIds: unclassified.map((f) => f.id)
        });
        toast.info(
          `Organizing ${unclassified.length} new ${
            unclassified.length === 1 ? "file" : "files"
          }. Insights update when it finishes.`
        );
      }
      await KeyFactsAPI.refresh(individualId);
      refetch();
    } catch (e) {
      toast.error(keyFactErrorText(e));
    } finally {
      setRefreshing(false);
    }
  };

  const body = (content: (loaded: KeyFactsCase) => ReactNode) =>
    error ? (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    ) : loading || !data ? (
      <LoadingState className="h-full" text="Loading Case Insights…" />
    ) : (
      content(data)
    );

  return (
    <Tabs
      value={view}
      onValueChange={(v) => setView(v as KeyFactsView)}
      className="flex h-full min-h-0 w-full flex-col gap-0 overflow-hidden bg-surface"
    >
      <div className="flex flex-none items-center gap-2 border-b border-line-1 px-3 py-1.5">
        <TabsList variant="pane" aria-label="Case Insights">
          {noticeCase ? (
            <TabsTrigger value="brief">{expecting} Brief</TabsTrigger>
          ) : null}
          <TabsTrigger value="facts">
            Insights
            {queue.length ? (
              <span className="rounded-full bg-warn-soft px-1.5 text-sm font-medium text-warn tabular-nums">
                {queue.length}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="case">On This Case</TabsTrigger>
        </TabsList>
        <div className="ml-auto flex items-center gap-1.5">
          {updating ? (
            <span className="flex items-center gap-1.5 text-sm text-fg-3">
              <Spinner className="size-3" /> Updating
            </span>
          ) : null}
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Recheck"
            title={recheckLabel}
            disabled={
              refreshing || updating || (organizing && workflows?.running)
            }
            onClick={() => void refresh()}
          >
            <RefreshCw className={refreshing ? "animate-spin" : undefined} />
          </Button>
        </div>
      </div>
      {noticeCase ? (
        <TabsContent value="brief" className="min-h-0 overflow-y-auto p-4">
          {body(() => (
            <NoticeBrief
              individualId={individualId}
              facts={facts}
              expecting={expecting}
              onOpen={onOpen}
              onPrompt={onPrompt}
              onReveal={onReveal}
            />
          ))}
        </TabsContent>
      ) : null}
      <TabsContent value="facts" className="min-h-0 overflow-y-auto pb-3">
        {body(() =>
          facts.length === 0 ? (
            <p className="py-12 text-center text-sm text-fg-3">
              No insights yet. They appear as documents and case details come
              in.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <NeedsYouStrip counts={counts} flow={flow} />
              <FactReference
                individualId={individualId}
                facts={facts}
                sections={sections}
                flow={flow}
                actions={actions}
                onOpen={onOpen}
              />
            </div>
          )
        )}
      </TabsContent>
      <TabsContent value="case" className="min-h-0 overflow-y-auto p-4">
        {body((loaded) => (
          <OnThisCase data={loaded} actions={actions} onSaved={refetch} />
        ))}
      </TabsContent>
    </Tabs>
  );
};
